<script setup lang="ts">
import { BCol, BLink, BRow } from 'bootstrap-vue-next';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import { fetchMyRegistration } from '~/api/transcendHackathon';
import ErrorState from '~/common/components/ErrorState.vue';
import ThemedOverlay from '~/common/components/ThemedOverlay.vue';
import { useVueData } from '~/common/useVueData';

import showcaseDetailsMarkdown from './transcend-hackathon-page/ShowcaseDetails.md?raw';
import showcaseRewardsMarkdown from './transcend-hackathon-page/ShowcaseRewards.md?raw';
import ShowcaseTrackModal from './transcend-hackathon-page/ShowcaseTrackModal.vue';
import TrackCard from './transcend-hackathon-page/TrackCard.vue';
import TrackSections from './transcend-hackathon-page/TrackSections.vue';
import TranscendBanner from './transcend-hackathon-page/TranscendBanner.vue';
import TranscendCountdown from './transcend-hackathon-page/TranscendCountdown.vue';
import { isShowcaseRegistrationOpen, transcendPhase } from './transcend-hackathon-page/transcendDates';

const DEVPOST_URL = 'https://gitlab-transcend.devpost.com/';
const PHASE_POLL_MS = 1000;

const { isSignedIn, isAdmin } = useVueData();

const now = ref(Date.now());
const phaseTimer = ref<ReturnType<typeof setInterval> | null>(null);
const registrationOpen = computed(() => isShowcaseRegistrationOpen(new Date(now.value)));
const trackUpcoming = computed(() => transcendPhase(new Date(now.value)) === 'upcoming');
const trackClosed = computed(() => !registrationOpen.value);

const showcaseModalVisible = ref(false);
const registrationStatus = ref('none');
const registrationError = ref(false);
const showcaseGroupUrl = ref('');
const showcaseProjectUrl = ref('');

const requireSignIn = (): boolean => {
  if (isSignedIn) {
    return true;
  }

  window.location.href = '/login?return=/transcend-hackathon';

  return false;
};

const openShowcaseRegistration = (): void => {
  if (!requireSignIn()) {
    return;
  }

  if (registrationStatus.value === 'approved' && showcaseProjectUrl.value) {
    window.open(showcaseProjectUrl.value, '_blank');

    return;
  }

  showcaseModalVisible.value = true;
};

const handleRegistered = ({ status, projectUrl }: { status: string; projectUrl: string }): void => {
  registrationStatus.value = status || 'pending';
  showcaseProjectUrl.value = projectUrl;
};

onBeforeUnmount(() => {
  if (phaseTimer.value) {
    clearInterval(phaseTimer.value);
  }
});

onMounted(async () => {
  phaseTimer.value = setInterval(() => {
    now.value = Date.now();
  }, PHASE_POLL_MS);

  if (isSignedIn) {
    try {
      const data = await fetchMyRegistration();

      if (data.registered) {
        registrationStatus.value = data.status || 'pending';
        showcaseGroupUrl.value = data.group_url || '';
        showcaseProjectUrl.value = data.project_url || '';
      }
    } catch {
      registrationError.value = true;
    }
  }
});
</script>

<template>
  <ThemedOverlay>
    <TranscendBanner class="mt-3 mb-2" />
    <div class="text-center mt-3">
      <h3>Join us from October 6, 2026</h3>
    </div>
    <TranscendCountdown />

    <p class="mt-3">
      This is the Transcend Hackathon, run on
      <a
        :href="DEVPOST_URL"
        target="_blank"
      >Devpost</a> with cash prizes.
      Looking for our regular quarterly hackathon instead? Head to the
      <BLink :to="{ name: 'hackathon' }">
        Community Hackathon
      </BLink>.
    </p>

    <ErrorState v-if="registrationError" />

    <BRow class="my-4">
      <BCol cols="12">
        <TrackCard
          title="Transcend Hackathon"
          action-name="Get started"
          button-variant="primary"
          :disabled="trackClosed"
          @action-click="openShowcaseRegistration"
        >
          <template v-if="trackClosed && !trackUpcoming">
            <p>
              <strong>This hackathon is now closed.</strong>
              A huge thank you to everyone who participated! Your work
              automating life after code means the world to us.
            </p>
          </template>
          <template v-else>
            <template v-if="trackUpcoming">
              <p>
                <strong>Registration opens on September 29, 2026
                  (10:00 UTC).</strong><br>
                Submissions open on October 5, 2026 (10:00 UTC).
              </p>
            </template>
            <p>
              Build agentic workflows on GitLab that automate any part of the
              software development lifecycle after the code is written, from
              review and testing through to deployment, monitoring, and
              incident response.
            </p>
            <p>
              You must also register on
              <a
                :href="DEVPOST_URL"
                target="_blank"
              >Devpost</a> to be eligible for cash prizes, and submit
              your final project there before October 27, 2026.
            </p>
          </template>
        </TrackCard>
      </BCol>
    </BRow>

    <BRow>
      <BCol cols="12">
        <TrackSections
          title="Transcend Hackathon"
          :details-markdown="showcaseDetailsMarkdown"
          :rewards-markdown="showcaseRewardsMarkdown"
        />
      </BCol>
    </BRow>

    <p
      v-if="isAdmin"
      class="text-end mt-2"
    >
      <BLink :to="{ name: 'admin-transcend-hackathon' }">
        Manage registrations
      </BLink>
    </p>

    <ShowcaseTrackModal
      v-model="showcaseModalVisible"
      :status="registrationStatus"
      :group-url="showcaseGroupUrl"
      :project-url="showcaseProjectUrl"
      @registered="handleRegistered"
    />
  </ThemedOverlay>
</template>
